import React, { useMemo, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline/index.js';
import { classes, padStart } from '../utils';
import { useFormContext } from 'react-hook-form';
import type { AppData, BBQSubmission } from '../App';
import * as z from 'zod/mini';
import type { Calculation } from './Modal';
import {
  isDateBookable,
  isTimeBlockedBySeason,
  stepBookableMonth,
} from '../season';

export const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

export function getDay(date: Date): (typeof DAYS)[number] {
  const index = date.getDay() ? date.getDay() - 1 : 6;
  return DAYS[index]!;
}

function monthYear(date: Date) {
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: 'long',
  }).format(date);
}

function weekdayDayMonth(date: Date) {
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
}

export function isoDate(date: Date) {
  return (
    padStart(date.getFullYear(), 4, '0') +
    '-' +
    padStart(date.getMonth() + 1, 2, '0') +
    '-' +
    padStart(date.getDate(), 2, '0')
  );
}

export function hourMinute(date: Date) {
  return (
    padStart(date.getHours().toString(), 2, '0') +
    ':' +
    padStart(date.getMinutes().toString(), 2, '0')
  );
}

interface SelectedMonth {
  year: number;
  month: number;
}

export const optionsSchema = z.array(
  z.object({
    bookingDate: z.coerce.date(),
    personCount: z.coerce.number(),
  })
);

export type Option = z.output<typeof optionsSchema>[number];

export const BOAT_CAPACITY = 10;

// Persons occupied by all bookings at a given time. Each booking is rounded UP
// to a whole boat (BOAT_CAPACITY persons): two groups of 5 occupy 20, not 10,
// because each group gets its own boat.
export function occupiedPersonsForTime(
  bookings: Option[],
  time: string
): number {
  return bookings
    .filter((b) => hourMinute(b.bookingDate) === time)
    .reduce(
      (sum, b) =>
        sum + Math.ceil((+b.personCount || 0) / BOAT_CAPACITY) * BOAT_CAPACITY,
      0
    );
}

// Whole boats still available at a given time: how many full BOAT_CAPACITY
// chunks fit in the location's remaining capacity. A new booking always needs
// at least one whole boat, so 0 here means the slot is fully booked even if a
// few individual seats are technically "free" (they belong to a boat that's
// already occupied by another group).
export function boatsLeftForTime(
  bookings: Option[],
  time: string,
  maxPeople: number
): number {
  const booked = occupiedPersonsForTime(bookings, time);
  return Math.max(0, Math.floor((maxPeople - booked) / BOAT_CAPACITY));
}

interface TimeOption {
  date: Date;
  disabled: boolean;
}

interface Calendar {
  loc: 'hanekenfaehr' | 'alter_hafen';
  // Total person capacity for this location, from the `?personen=` query param
  // (see App.tsx). A time slot is fully booked once no whole boat's worth of
  // capacity remains.
  maxPeople: number;
  // Bookings per date, fetched and owned by the parent (Configurator) so the
  // person slider and the time slots share one source of truth.
  taken: Map<string, Option[]>;
}

export default function Calendar({ loc, maxPeople, taken }: Calendar) {
  const appData: AppData = (window as any).bbqData;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // const firstSelected = (() => {
  //   let v = today;
  //   while (!appData.days.some(({ day }) => day === getDay(v))) {
  //     v.setDate(v.getDate() + 1);
  //   }
  //   return v;
  // })();

  const { setValue, watch, register } = useFormContext<BBQSubmission>();
  const watchDate = watch('date');
  const watchTime = watch('time');
  const selectedDate = new Date(watchDate);

  const [selectedMonth, setSelectedMonth] = useState<SelectedMonth>({
    month: selectedDate.getMonth(),
    year: selectedDate.getFullYear(),
  });

  const firstDay = new Date(selectedMonth.year, selectedMonth.month, 1);
  const pad = firstDay.getDay() ? firstDay.getDay() - 1 : 6;
  const weekday = DAYS[(selectedDate.getDay() + 6) % 7];

  const lastDay = new Date(selectedMonth.year, selectedMonth.month + 1, 0);

  const selectedTaken = taken.get(isoDate(selectedDate));

  const valid = (d: Date) => {
    if (!appData.days.map(({ day }) => day).includes(getDay(d))) return false;
    if (d < today) return false;
    if (!isDateBookable(d, loc, appData)) return false;
    if (d.getFullYear() === 2026 && d.getMonth() === 3 && loc === 'alter_hafen')
      return false;

    return true;
  };

  // A slot is disabled once no whole boat's worth of capacity remains for it —
  // a new group can never book a partial boat, so a handful of leftover seats
  // don't make a slot bookable.
  const currentOptions = useMemo<TimeOption[]>(() => {
    const index = appData.days.findIndex((d) => d.day === weekday);

    if (index === -1) return [];
    if (!valid(selectedDate)) return [];
    if (!selectedTaken) return [];

    return appData.days[index]!.times.filter(
      ({ time }) => !isTimeBlockedBySeason(selectedDate, time)
    ).map(({ time }): TimeOption => {
      const boatsLeft = boatsLeftForTime(selectedTaken, time, maxPeople);

      const [hour, minute] = time.split(':', 2).map((v) => +v);
      const d = new Date(selectedDate);
      d.setHours(hour!, minute!, 0, 0);

      return {
        date: d,
        disabled: boatsLeft < 1,
      };
    });
  }, [weekday, selectedDate, selectedTaken, maxPeople]);

  return (
    <div className="cf-cal tw:flex tw:max-md:flex-col tw:mx-auto tw:p-8 tw:w-full tw:max-w-[360px] tw:md:max-w-[660px] tw:bg-white tw:text-black tw:rounded-xl">
      <div className="cf-cal-left cf-cal-side">
        <div className="cf-cal-bar cf-cal-left-bar">
          <button
            type={'button'}
            disabled={
              new Date(
                selectedMonth.year,
                selectedMonth.month,
                ...Array(5).fill(0)
              ) <= today
            }
            onClick={() =>
              // Previous bookable month for this location (skips closed ones)
              setSelectedMonth(({ year, month }) =>
                stepBookableMonth(year, month, -1, loc, appData)
              )
            }
            className="cf-cal-chev tw:flex-center">
            <ChevronLeftIcon className={'tw:size-6'} />
          </button>

          <span>
            {monthYear(new Date(selectedMonth.year, selectedMonth.month, 1))}
          </span>

          <button
            type={'button'}
            disabled={
              new Date(
                selectedMonth.year,
                selectedMonth.month,
                ...Array(5).fill(0)
              ) > new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000)
            }
            onClick={() =>
              // Next bookable month for this location (skips closed ones)
              setSelectedMonth(({ year, month }) =>
                stepBookableMonth(year, month, 1, loc, appData)
              )
            }
            className="cf-cal-chev tw:flex-center">
            <ChevronRightIcon className={'tw:size-6'} />
          </button>
        </div>

        <div className="tw:flex-center">
          <div className="cf-cal-left-cal">
            {DAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}

            {Array(pad)
              .fill(null)
              .map((_, i) => (
                <span className="cf-cal-mt" key={i} />
              ))}

            {Array(lastDay.getDate())
              .fill(null)
              .map((_, i) => i + 1)
              .map(
                (d) =>
                  new Date(
                    selectedMonth.year,
                    selectedMonth.month,
                    d,
                    ...Array(4).fill(0)
                  )
              )
              .map((d) => {
                return (
                  <input
                    required
                    {...register('date')}
                    type={'radio'}
                    disabled={!valid(d) || d < today}
                    className={classes(
                      'cf-cal-day tw:flex-center tw:active:scale-90 tw:transition-transform tw:checked:selected tw:disabled:pointer-events-none'
                    )}
                    onInput={() => {
                      setValue('time', undefined as any);
                    }}
                    // onChange={(ev) => {
                    //   const v = +new FormData(ev.target.form!)?.get('date24')!;
                    //   if (v !== d.getDate()) return;
                    //
                    //   setSelectedDate(d);
                    //   setValue('date', undefined as any);
                    //
                    //   if (!taken.has(d.toJSON())) {
                    //     fetchOptionsFor(d).catch(console.error);
                    //   }
                    // }}
                    data-text={d.getDate()}
                    value={isoDate(d)}
                    key={d.toJSON()}
                  />
                );
              })}

            {Array(6 * 7 - pad - lastDay.getDate())
              .fill(null)
              .map((_, i) => (
                <span className="cf-cal-mt" key={i} />
              ))}
          </div>
        </div>
      </div>

      <div className="cf-cal-right cf-cal-side tw:min-h-[350px]">
        <div className="cf-cal-bar cf-cal-right-bar">
          <span>{weekdayDayMonth(selectedDate)}</span>
        </div>

        <div
          className={classes(
            'cf-cal-options',
            currentOptions.length ? 'tw:opacity-100' : 'tw:opacity-0',
            'tw:transition-opacity'
          )}>
          {currentOptions?.map((option) => (
            <input
              key={hourMinute(option.date)}
              type={'radio'}
              required
              disabled={option.disabled}
              {...register('time')}
              value={hourMinute(option.date)}
              data-text={`${hourMinute(option.date)} Uhr`}
              className={classes(
                'cf-cal-time tw:w-full tw:min-h-[3.75rem]',
                'tw:rounded-2xl tw:font-bold tw:p-4 tw:transition-all tw:active:scale-[.98]',
                'tw:checked:text-white tw:checked:bg-primary tw:border-[1px] tw:checked:border-transparent',
                'tw:text-black tw:bg-transparent tw:border-primary',
                'tw:disabled:opacity-40 tw:disabled:pointer-events-none'
              )}
            />
          ))}
          <input
            type={'radio'}
            required
            {...register('time')}
            className={
              'tw:pointer-events-none tw:absolute tw:-z-50 tw:appearance-none'
            }
          />
        </div>
      </div>
    </div>
  );
}
